import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Box, Skeleton, alpha } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { DRILLDOWN_MODULES, buildDrilldownPath, useDrilldownContext } from './dashboardDrilldown';

/**
 * `drilldown={{ module, metric, status? }}` makes the card open that module's list,
 * filtered to the records the card counted (see dashboardDrilldown.js).
 */
const StatCard = ({ title, value, icon, color = 'primary', subtitle, loading, gradient, drilldown }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const ctx = useDrilldownContext();

  const target = drilldown && ctx && hasPermission(DRILLDOWN_MODULES[drilldown.module]?.permission)
    ? buildDrilldownPath({ ...drilldown, label: drilldown.label ?? (typeof title === 'string' ? title : '') }, ctx)
    : null;

  const clickable = Boolean(target) && !loading;

  return (
  <Card
    {...(clickable && {
      role: 'link',
      tabIndex: 0,
      onClick: () => navigate(target),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(target);
        }
      },
    })}
    sx={{
      cursor: clickable ? 'pointer' : undefined,
      transition: 'box-shadow 0.15s, border-color 0.15s',
      '&:hover': clickable ? { boxShadow: 3, borderColor: 'primary.main' } : undefined,
      height: '100%',
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      position: 'relative',
      background: gradient
        ? `linear-gradient(135deg, ${alpha('#1976d2', 0.08)} 0%, ${alpha('#1976d2', 0.02)} 100%)`
        : undefined,
      border: (t) => `1px solid ${t.palette.divider}`,
    }}
  >
    <CardContent
      sx={{
        position: 'relative',
        zIndex: 1,
        py: 1.25,
        px: 1.75,
        '&:last-child': { pb: 1.25 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75, mb: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={500}
          sx={{
            flex: 1,
            minWidth: 0,
            lineHeight: 1.35,
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: `${color}.main`,
            color: 'white',
            borderRadius: 1.5,
            p: { xs: 0.5, sm: 0.65 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& svg': { fontSize: { xs: 16, sm: 18 } },
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography
        variant="h5"
        component="div"
        sx={{
          fontWeight: 700,
          mb: 0.25,
          letterSpacing: '-0.02em',
          fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
          lineHeight: 1.2,
          overflowWrap: 'break-word',
        }}
      >
        {loading ? <Skeleton width={72} height={28} /> : value}
      </Typography>
      {subtitle != null && (
        <Typography variant="caption" color="text.secondary">
          {loading ? <Skeleton width={120} /> : subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
  );
};

export default StatCard;
